<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img class="absolute -left-[1vw] top-[-12vh] w-[20vw] opacity-80" src="/images/bg-visual-left-top.webp" alt="" />
        <img class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60" src="/images/bg-visual-right-top.webp" alt="" />
      </div>
    </div>

    <section id="hero" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-6 lg:gap-y-10 items-center mt-16">
        <div class="flex items-center gap-x-5">
          <div class="w-14 h-14 rounded-xl bg-gradient-to-br from-[#833ab4] via-[#fd1d1d] to-[#fcb045] flex items-center justify-center drop-shadow">
            <i class="bi bi-instagram text-white text-2xl"></i>
          </div>
          <div>
            <span class="text-xs font-semibold text-lighter uppercase tracking-widest">{{ t('pages.info.integrations_detail.instagram.hero.category') }}</span>
            <h1 class="text-[32px] leading-[38px] lg:text-[60px] lg:leading-[66px] font-bold text-dark font-markPro tracking-[-0.1rem]">
              Bistroo <span class="text-gradient-primary">× Instagram</span>
            </h1>
          </div>
        </div>

        <p class="text-lighter text-lg lg:text-xl font-semibold max-w-[620px] text-center">
          {{ t('pages.info.integrations_detail.instagram.hero.description') }}
        </p>

        <div class="flex flex-col gap-y-10 mt-4">
          <div class="flex gap-x-3 items-center flex-row justify-center">
            <a
              v-track-event="'onboarding_start'"
              href="https://merchant.bistroo.nl/register"
              class="btn btn-lg btn-primary whitespace-nowrap">
              {{ t('main.cta_block.get_started_for_free') }}
            </a>
            <a
              href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1"
              target="_blank"
              class="btn btn-lg border-2 border-gray-300/60 whitespace-nowrap"
              >{{ t('main.actions.product_demo') }}
            </a>
          </div>
          <div class="flex flex-wrap gap-x-6 gap-y-2">
            <div class="flex items-center gap-x-2 text-sm text-lighter font-semibold">
              <i class="bi bi-check-circle-fill text-gradient-primary"></i>
              <span>{{ t('main.integration.check_1') }}</span>
            </div>
            <div class="flex items-center gap-x-2 text-sm text-lighter font-semibold">
              <i class="bi bi-check-circle-fill text-gradient-primary"></i>
              <span>{{ t('main.integration.check_2') }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="about" class="max-w-[1080px] lg:m-[auto]">
      <div class="grid lg:grid-cols-2 gap-10 items-start">
        <div class="flex flex-col gap-y-6">
          <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
            {{ t('pages.info.integrations_detail.instagram.about.title') }}
          </h2>
          <p class="text-lighter font-semibold leading-relaxed">
            {{ t('pages.info.integrations_detail.instagram.about.p1') }}
          </p>
          <p class="text-lighter font-semibold leading-relaxed">
            {{ t('pages.info.integrations_detail.instagram.about.p2') }}
          </p>
        </div>

        <div class="bg-white rounded-2xl drop-shadow p-8 flex flex-col gap-y-5">
          <h3 class="font-markPro font-bold text-xl text-dark">{{ t('pages.info.integrations_detail.instagram.about.benefits_title') }}</h3>
          <ul class="flex flex-col gap-y-4">
            <li v-for="benefit in benefits" :key="benefit" class="flex gap-x-3 items-start">
              <i class="bi bi-check-circle-fill text-gradient-primary text-lg mt-0.5 shrink-0"></i>
              <span class="text-lighter font-semibold">{{ benefit }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <WordCloud integration-key="instagram" />

    <section id="usp" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-xs font-semibold text-lighter uppercase tracking-widest">{{ t('main.integration.advantages_label') }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.info.integrations_detail.instagram.usp.heading') }}
        </h2>
        <p class="text-lighter font-semibold max-w-[600px]">{{ t('main.integration.advantages_subtitle') }}</p>
      </div>
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
        <div v-for="usp in usps" :key="usp.title" class="group bg-white p-8 rounded-2xl border border-gray-100 hover:border-[#FF3696]/20 hover:drop-shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col gap-y-4">
          <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF3696]/10 to-[#FF7C53]/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <i :class="`bi ${usp.icon} text-[#FF3696] text-xl`"></i>
          </div>
          <h4 class="font-markPro font-bold text-dark text-lg">{{ usp.title }}</h4>
          <p class="text-lighter text-sm font-semibold leading-relaxed">{{ usp.text }}</p>
        </div>
      </div>
    </section>

    <section id="setup" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-xs font-semibold text-lighter uppercase tracking-widest">{{ t('main.integration.setup_label') }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.info.integrations_detail.instagram.setup.heading') }}
        </h2>
      </div>

      <div class="grid md:grid-cols-5 gap-4 w-full">
        <div v-for="(step, i) in setupSteps" :key="i" class="group bg-white p-8 rounded-2xl border border-gray-100 hover:border-[#FF3696]/20 hover:drop-shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col gap-y-4">
          <span class="text-3xl font-bold font-markPro text-gradient-primary">{{ i + 1 }}</span>
          <p class="text-dark font-semibold text-sm leading-relaxed">{{ step }}</p>
        </div>
      </div>
    </section>

    <!-- <section id="testimonial" class="max-w-[1080px] lg:m-[auto]">
      <div class="bg-[#E3EEF7] flex flex-col lg:flex-row items-center px-8 lg:px-16 py-10 lg:py-14 gap-10 lg:gap-16 rounded-2xl">
        <div class="shrink-0">
          <div class="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-[#FF3696] to-[#FF7C53] flex items-center justify-center text-white text-3xl font-bold font-markPro">
            N
          </div>
        </div>
        <div class="flex flex-col gap-y-5 max-md:text-center">
          <p class="text-xl lg:text-2xl font-semibold font-markPro tracking-[-0.03rem] leading-snug">
            "Instagram is waar mijn klanten me vinden. De bestelknop in mijn bio levert iedere dag nieuwe bestellingen op van mensen die mijn gerechten voorbij zien komen."
          </p>
          <p class="text-lg">
            <span class="font-semibold">Nadia Jansen,</span>
            <span class="text-lighter"> eigenaar Sushi by Nadia</span>
          </p>
        </div>
      </div>
    </section> -->

    <section id="faq" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">{{ t('main.integration.faq_heading') }}</h2>
      </div>

      <FaqAccordion :items="faqs" />
    </section>

    <RelatedIntegrations exclude="instagram" />

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div class="parallax" data-parallax data-parallax-speed="0.05" data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]" src="/images/bg-visual-left-top.webp" alt="" />
            <img class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]" src="/images/bg-visual-right-top.webp" alt="" />
          </div>
        </div>
        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h4 class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap"> {{ t('main.cta_block.its_free') }}</span>
          </h4>
          <p class="text-lg lg:text-[24px] text-lighter font-semibold">{{ t('main.cta_block.start_generating_more_orders') }}</p>
          <div class="flex flex-col sm:flex-row gap-4 mt-4">
            <a v-track-event="'onboarding_start'" href="https://merchant.bistroo.nl/register" class="btn btn-lg btn-primary flex items-center gap-x-3">
              <span>{{ t('main.cta_block.get_started_for_free') }}</span>
              <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
            </a>
            <a href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1" target="_blank" class="btn btn-lg border-2 border-gray-300/60 whitespace-nowrap">
              {{ t('main.actions.product_demo') }}
            </a>
          </div>
          <p class="text-sm text-lighter font-semibold mt-2">{{ t('main.integration.cta_footer_text') }}</p>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import FaqAccordion from '@/components/FaqAccordion.vue'
import RelatedIntegrations from '@/components/RelatedIntegrations.vue'
import WordCloud from '@/components/WordCloud.vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const { t, tm } = useI18n()

const usps = computed(() => tm('pages.info.integrations_detail.instagram.usp.items') as { icon: string; title: string; text: string }[])
const setupSteps = computed(() => tm('pages.info.integrations_detail.instagram.setup.steps') as string[])
const faqs = computed(() => tm('pages.info.integrations_detail.instagram.faq.items') as { q: string; a: string }[])
const benefits = computed(() => tm('pages.info.integrations_detail.instagram.about.benefits') as string[])


useHead(computed(() => ({
  title: t('pages.info.integrations_detail.instagram.seo.title'),
  meta: [{ name: 'description', content: t('pages.info.integrations_detail.instagram.seo.description') }],
})))
</script>

<style scoped>
@reference "@/assets/css/main.css";
</style>
